import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import '../styles/AdminLoginPage.css';

// Gate debug logging behind NODE_ENV for development use only
const debugLog = (...args: any[]) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(...args);
  }
};

debugLog('[DEBUG AdminLoginPage] Component loaded');

const AdminLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated, loading, error: authError, clearError } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  debugLog('[DEBUG AdminLoginPage] Render state:', { isAuthenticated, loading, isSubmitting });

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && !loading) {
      debugLog('[DEBUG AdminLoginPage] User already authenticated, redirecting...');
      navigate('/admin/reviews', { replace: true });
    }
  }, [isAuthenticated, loading, navigate]);

  // Clear error when component unmounts
  useEffect(() => {
    return () => {
      clearError();
    };
  }, [clearError]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    debugLog('[DEBUG AdminLoginPage] Login form submitted');

    if (isSubmitting || loading) {
      debugLog('[DEBUG AdminLoginPage] Already submitting or loading, ignoring submit');
      return;
    }

    setIsSubmitting(true);
    clearError();

    try {
      debugLog('[DEBUG AdminLoginPage] Attempting login for:', username);
      await login(username, password);

      // If successful, navigate to admin reviews
      debugLog('[DEBUG AdminLoginPage] Login successful, navigating to admin reviews');
      navigate('/admin/reviews', { replace: true });
    } catch (err) {
      // Error is handled by AuthContext and displayed via authError
      console.error('[DEBUG AdminLoginPage] Login failed:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show loading spinner while checking auth state
  if (loading) {
    return (
      <div className="admin-login-page">
        <div className="container">
          <header className="admin-header">
            <h1>Admin Login</h1>
          </header>
          <div className="login-form-container">
            <p>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-login-page">
      <div className="container">
        <header className="admin-header">
          <h1>Admin Login</h1>
        </header>

        <div className="login-form-container">
          {authError && <div className="error-message">{authError}</div>}

          <form className="login-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="username">Email</label>
              <input
                type="email"
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                disabled={isSubmitting}
                placeholder="admin@example.com"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isSubmitting}
                required
              />
            </div>

            <button
              type="submit"
              className="login-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Logging in...' : 'Login'}
            </button>

            {/* TODO: Add Forgot Password functionality */}
            {/* <a href="/admin/forgot-password" className="forgot-password-link">
              Forgot Password?
            </a> */}
          </form>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
